import React from 'react';
import {
  portalDs,
  PortalDsButton,
  PortalDsField,
  PortalDsSelect,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';

const PAGE_SIZE_OPTIONS = [
  { value: '25', label: '25 per page' },
  { value: '50', label: '50 per page' },
  { value: '100', label: '100 per page' },
];

export interface ManageListPaginationProps {
  page: number;
  totalPages: number;
  totalObjects: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  disabled?: boolean;
}

const ManageListPagination: React.FC<ManageListPaginationProps> = ({
  page,
  totalPages,
  totalObjects,
  pageSize,
  onPageChange,
  onPageSizeChange,
  disabled = false,
}) => {
  const safeTotalPages = Math.max(1, totalPages);
  const start = totalObjects === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalObjects);

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <p className={cn(portalDs.bodySm, 'text-[13px]')}>
        {totalObjects === 0 ? (
          'No results'
        ) : (
          <>
            Showing
            {' '}
            <span className="font-semibold text-[#0D0F1A]">
              {start}
              –
              {end}
            </span>
            {' '}
            of
            {' '}
            <span className="font-semibold text-[#0D0F1A]">
              {totalObjects}
            </span>
          </>
        )}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <PortalDsField
          label="Page size"
          filter
          className="min-w-[140px]"
        >
          <PortalDsSelect
            value={String(pageSize)}
            disabled={disabled}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </PortalDsSelect>
        </PortalDsField>
        <PortalDsButton
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </PortalDsButton>
        <span className={cn(portalDs.caption, 'self-center tabular-nums text-[#5E6278]')}>
          Page
          {' '}
          {page}
          {' '}
          /
          {' '}
          {safeTotalPages}
        </span>
        <PortalDsButton
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || page >= safeTotalPages || totalObjects === 0}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </PortalDsButton>
      </div>
    </div>
  );
};

export default ManageListPagination;
